import { FC, useState, useCallback, useEffect } from "react";
import { Avatar, Box, Tooltip } from "@mui/material";
import CustomNetworkImage from "../CustomNetworkImage/CustomNetworkImage";
import { useAppSelector } from "../../redux-store/CustomReduxHooks";
import ABCDLogo from "../../assets/images/ABC-Service.jpg";
import SearchIcon from "@mui/icons-material/Search";
import TriggerGTAEvent from "../../utils/GTA";
import { isEmpty } from "lodash";
import { executeIfNotPseudoUser, getInitials } from "../../utils/helper";
import { useNavigate } from "react-router";
import notificationBellIcon from "../../assets/icons/notification-bell.svg";
import notificationBellIconUnread from "../../assets/icons/notification-bellIcon-unread.svg";
import { GTAScreenType, SCREEN_TYPE } from "../../utils/constants";
import AppTrackerIcon from "../../assets/icons/App-tracker-icon.svg";
import { R_GeneralElementsRoutes, R_HomeRoutes } from "../../utils/Routes";
import threeLinesIcon from "../../assets/icons/three-lines.svg";
import RevampOnboardingPopup from "../../pages/RevampOnboarding/RevampOnboardingPopup";
import NotificationsPage from "../../features/Feature_GeneralAppElements/NotificationsPage/NotificationsPage";
import AdaptiveScreenSearch from "../../features/Feature_GeneralAppElements/BasicSearch/AdaptiveScreenSearch";
import clevertap, { triggerCleverTapEvent } from "../../utils/CleverTap";
import HelpAndSupportIcon from "../../assets/icons/help-and-support-icon.svg";
import HamburgerMenuDialog from "../../features/Feature_GeneralAppElements/HamburgerMenuDialog/HamburgerMenuDialog";
import { useSearchParams } from "react-router-dom";
import "./NavbarDesktop.scss";
import ChatBot from "../../features/Feature_GeneralAppElements/ChatBot/ChatBot";

const NavbarDesktop: FC = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [openedFromHome, setOpenedFromHome] = useState(false);
  const [hamburgerMenuDefaultComponent, setHamburgerMenuDefaultComponent] =
    useState<string>("main-menu");
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [notificationOpen, setNotificationOpen] = useState<boolean>(false);

  const [openOnboardingPopup, setOpenOnboardingPopup] =
    useState<boolean>(false);
  const [URLSearchParams] = useSearchParams();
  const action = URLSearchParams.get("action") || "";
  const cmsData = useAppSelector((state) => state.CmsSlice.CmsPageVariableData);
  const navigate = useNavigate();
  const userDetails = useAppSelector((state) => state.CsAppSlice.userDetails);
  const isPseudoUserLoggedIn = useAppSelector(
    (state) => state.PseudoLoginSlice.isPseudoLoggedIn,
  );

  const notificationIcon =
    cmsData.find((i) => i.attributes.cmsType === "notification-icon")
      ?.attributes?.image?.data?.attributes.url ?? notificationBellIcon;

  const notificationUnreadIcon =
    cmsData.find((i) => i.attributes.cmsType === "notification-icon-unread")
      ?.attributes?.image?.data?.attributes.url ?? notificationBellIconUnread;

  const cmsLogo =
    cmsData.find((i) => i.attributes.cmsType === "ABC Logo")?.attributes.image
      .data?.attributes.url ?? "";

  useEffect(() => {
    if (
      [
        "FAQs",
        "hamburgerMenu",
        "myProfile",
        "utilities",
        "helpAndSupport",
        "appSettings",
        "logout",
      ].includes(action)
    ) {
      setOpen(true);
    }
    if (action === "search") {
      setIsSearching(true);
    }
  }, []);

  const handleNotificationClick = useCallback(() => {
    triggerCleverTapEvent("serv_notification_bell_icon_click", {
      ScreenType: SCREEN_TYPE.generalAppType,
      ScreenName: "Dashboard Screen",
      isPrevPageNameRequired: true,
      ctaText: "notification_bell_icon",
    });
    TriggerGTAEvent("serv_notification_bell_icon_click", {
      screen_type: GTAScreenType.DashboardScreenType,
      screen_name: "servicing_homescreen",
      CTA_text: "notification_bell_icon",
    });
    setNotificationOpen(true);
  }, []);

  const handleSearchPopOpen = (value: boolean) => {
    setIsSearching(value);
    if (action === "search") {
      navigate(R_HomeRoutes.home, { replace: true });
    }
  };

  const imageStyle: React.CSSProperties = {
    marginLeft: "-0.3125rem",
    width: "7.125rem",
    marginRight: "0.3125rem",
    height: "2.75rem",
  };

  return (
    <>
      <Box className="navbar-desktop">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          marginX="1.5rem"
        >
          <Box display="flex" alignItems="center">
            <CustomNetworkImage
              src={cmsLogo}
              fallBackImage={<img src={ABCDLogo} alt="" style={imageStyle} />}
              alt="aditya-birla"
              style={imageStyle}
            />
          </Box>
          <Box display="flex" alignItems="center" gap="1rem">
            <Tooltip title="Search" arrow>
              <Box className="search-header-icon-wrapper">
                <SearchIcon
                  className="search-header-icon"
                  onClick={() => {
                    TriggerGTAEvent("hd_search_icon_click", {
                      screen_type: GTAScreenType.DashboardScreenType,
                      screen_name: "servicing_homescreen",
                    });
                    if (
                      !isEmpty(userDetails?.customerAndLobDetails) &&
                      !userDetails?.IS_USER_REGISTERED &&
                      !isPseudoUserLoggedIn
                    ) {
                      setOpenOnboardingPopup(true);
                      return;
                    } else {
                      if (!isPseudoUserLoggedIn) {
                        setIsSearching(true);
                      }
                    }
                  }}
                />
              </Box>
            </Tooltip>

            <Tooltip title="My Requests" arrow>
              <Box className="search-header-icon-wrapper">
                <img
                  className="request-header-icon"
                  src={AppTrackerIcon}
                  alt=""
                  onClick={() => {
                    executeIfNotPseudoUser(() => {
                      TriggerGTAEvent("hd_my_requests_cta_click", {
                        screen_type: GTAScreenType.DashboardScreenType,
                        screen_name: "servicing_homescreen",
                        CTA_text: "my_requests_icon",
                        isPrevPageNameRequired: true,
                      });
                      navigate(R_GeneralElementsRoutes.appTracker);
                    }, isPseudoUserLoggedIn);
                  }}
                />
              </Box>
            </Tooltip>

            <Tooltip title="Notifications" arrow>
              <Box
                className="search-header-icon-wrapper"
                id="notification-panel1"
                onClick={() => {
                  executeIfNotPseudoUser(() => {
                    handleNotificationClick();
                  }, isPseudoUserLoggedIn);
                }}
              >
                <div className="notification-icon-container">
                  <CustomNetworkImage
                    src={
                      clevertap?.getUnreadInboxMessages() !== undefined
                        ? Object.keys(clevertap?.getUnreadInboxMessages())
                            ?.length > 0
                          ? notificationUnreadIcon
                          : notificationIcon
                        : notificationIcon
                    }
                    alt=""
                  />
                  {clevertap?.getUnreadInboxMessages() !== undefined &&
                    Object.keys(clevertap?.getUnreadInboxMessages())?.length >
                      0 && (
                      <span className="notification-badge">
                        {Object.keys(clevertap?.getUnreadInboxMessages())
                          ?.length > 5
                          ? "5+"
                          : Object.keys(clevertap?.getUnreadInboxMessages())
                              ?.length}
                      </span>
                    )}
                </div>
              </Box>
            </Tooltip>

            <Tooltip title="Help" arrow>
              <Box className="search-header-icon-wrapper">
                <img
                  className="help-header-icon"
                  src={HelpAndSupportIcon}
                  alt=""
                  onClick={() => {
                    executeIfNotPseudoUser(() => {
                      TriggerGTAEvent("hd_navigation_cta_click", {
                        screen_type: GTAScreenType.DashboardScreenType,
                        screen_name: "servicing_homescreen",
                        CTA_text: "help_icon",
                        isPrevPageNameRequired: true,
                      });
                      setOpenedFromHome(true);
                      setHamburgerMenuDefaultComponent("help-&-support");
                      setOpen(true);
                    }, isPseudoUserLoggedIn);
                  }}
                />
              </Box>
            </Tooltip>

            <Box position="relative">
              <Avatar
                className="menu-icon"
                onClick={() => {
                  executeIfNotPseudoUser(() => {
                    TriggerGTAEvent("serv_hamburger_menu_icon_click", {
                      screen_type: GTAScreenType.DashboardScreenType,
                      screen_name: "servicing_homescreen",
                      CTA_text: "hamburger_menu_icon",
                      isPrevPageNameRequired: true,
                    });
                    setHamburgerMenuDefaultComponent("main-menu");
                    setOpen(true);
                  }, isPseudoUserLoggedIn);
                }}
              >
                {getInitials(
                  userDetails?.IS_USER_REGISTERED
                    ? userDetails?.name
                      ? userDetails?.name
                      : "User"
                    : "User",
                )}
              </Avatar>
              <img
                src={threeLinesIcon}
                className="three-lines-icon"
                alt="three-lines-icon"
              />
            </Box>
          </Box>
        </Box>
      </Box>
      {open === false && <ChatBot />}
      {open && (
        <HamburgerMenuDialog
          open={open}
          setOpen={(value: boolean) => {
            setOpen(value);
            if (!value) {
              setOpenedFromHome(false);
            }
          }}
          className="adaptive-screen-menu"
          fromHomeSection={openedFromHome}
          defaultVisibleComponent={hamburgerMenuDefaultComponent}
        />
      )}
      {isSearching && (
        <AdaptiveScreenSearch
          open={isSearching}
          handleOpen={handleSearchPopOpen}
        />
      )}
      {notificationOpen && (
        <NotificationsPage
          open={notificationOpen}
          handleOpen={setNotificationOpen}
          className="adaptive-notification"
        />
      )}
      {openOnboardingPopup && (
        <RevampOnboardingPopup
          open={openOnboardingPopup}
          handleOpen={setOpenOnboardingPopup}
        />
      )}
    </>
  );
};

export default NavbarDesktop;
